import { FIDescription, Icon, IconLib, Portal, classNames, getButton, stopEvent } from '@fold-dev/core'
import { useMemo, useRef } from 'react'
import {
    TbCheckbox,
    TbChecks,
    TbCircleCheck,
    TbClick,
    TbCornerDownLeft,
    TbCornerDownRight,
    TbExternalLink,
    TbHeart,
    TbLine,
    TbList,
    TbListCheck,
    TbMailBolt,
    TbMenu2,
    TbMenu3,
    TbMenu4,
    TbMessageCircle,
    TbPaperclip,
    TbRotate2,
    TbSubtask,
    TbTriangleFilled,
    TbTriangleInvertedFilled,
} from 'react-icons/tb'
import { Bars, Check, DateButton, LabelButton, TodoTaskEditor, TodoTypes, UserButton } from '../'
import { isRichTextBlank, openLinkInText } from '../../helpers'
import { BsTriangleFill } from 'react-icons/bs'
import { GoTriangleDown } from 'react-icons/go'

export type TodoTaskItemProps = {
    // general state
    edit?: boolean
    parentSelected?: boolean
    selected?: boolean
    showCollapse?: boolean
    subtaskAmount?: number
    // event handlers
    onEdit: (e) => void
    onEditSave: (e) => void
    onEditCancel: (e) => void
    onComplete: (e) => void
    onCollapse: (e) => void
    onIndent: (e) => void
    onOutdent: (e) => void
    onMouseDown: (e) => void
    // toolbar buttons
    onOpen: () => void
    onLabelAdd: (e) => void
    onLabelDelete: (e) => void
    onUserAdd: (e) => void
    onUserDelete: (e) => void
    onDateChange: (e) => void
    // from context
    noTaskLabels?: boolean
    kanbanMode?: boolean
    //DEBUG
    position?: number
} & TodoTypes.Task

export const TodoTaskItem = (props: any) => {
    const {
        // task state
        id,
        title,
        description,
        color,
        priority,
        type,
        complete,
        read,
        points,
        complexity,
        collapsed,
        editable,
        parentTaskId,
        start,
        end,
        repeat,
        wholeDay,
        externalEmail,
        users = [],
        labels = [],
        badges = [],
        comments = 0,
        teamId = null,
        projectId = null,
        projectName,
        projectColor,
        childTaskCount,
        // general state
        edit,
        parentSelected,
        selected,
        showCollapse,
        subtaskAmount,
        visibleFields,
        // event handlers
        onEdit,
        onEditSave,
        onEditCancel,
        onComplete,
        onCollapse,
        onIndent,
        onOutdent,
        onMouseDown,
        // toolbar buttons
        onOpen,
        onLabelAdd,
        onLabelDelete,
        onUserAdd,
        onUserDelete,
        onDateChange,
        // from context
        noTaskLabels,
        noTaskUsers,
        noTaskDates,
        kanbanMode,
        showProjectName,
        // DEBUG
        position,
    } = props
    // attachments might be actual attachments being synced from other clients
    const attachments = useMemo(() => {
        if (typeof props.attachments == 'object') {
            return props.attachments?.length || 0
        } else {
            return props.attachments || 0
        }
    }, [props.attachments])
    const hasDescription = useMemo(() => !isRichTextBlank(description), [description])
    const isHeading = useMemo(() => String(title).endsWith(':'), [title, kanbanMode])
    const isLiked = useMemo(() => comments > 0, [comments])
    const showLabels = useMemo(
        () => !noTaskLabels && (kanbanMode && labels.length == 0 ? false : true),
        [noTaskLabels, kanbanMode, labels.length]
    )
    const typeStyles: any = useMemo(() => {
        return {
            '--f-todo-task-item-project-color':
                type == 'milestone'
                    ? 'var(--f-color-success)'
                    : type == 'blocker'
                    ? 'var(--f-color-danger)'
                    : 'var(--f-color-accent)',
        }
    }, [type])
    const className = classNames(
        {
            'f-todo-task-item': true,
            'f-row': true,
            'is-selected': selected,
            'is-parent-selected': parentSelected,
            'is-indent': true,
            'is-edit': edit,
            'is-heading': isHeading,
            'is-complete': complete,
            'is-unread': !read,
            'is-milestone': type == 'milestone',
            'is-blocker': type == 'blocker',
        },
        [props.className]
    )

    return (
        <div
            tabIndex={0}
            className={className}
            onMouseDown={onMouseDown}
            onClick={onOpen}>
            <div className="f-row f-todo-task-item__inner">
                <div
                    className="f-row f-todo-task-item__collapse f-buttonize"
                    onClick={onCollapse}>
                    {showCollapse && (
                        <Icon
                            size="xs"
                            icon={GoTriangleDown}
                            className={`f-todo-task-item__collapse-icon ${collapsed ? 'is-collapsed' : ''}`}
                        />
                    )}
                </div>

                {!kanbanMode && !isHeading && (
                    <div className="f-todo-task-item__checkbox f-row">
                        <Check
                            priority={priority}
                            onCheck={onComplete}
                            checked={complete}
                            type={type}
                            id={id}
                        />
                    </div>
                )}

                <div className="f-todo-task-item__main-outer f-row">
                    <div className="f-todo-task-item__main">
                        <div className="f-todo-task-item__main-inner">
                            {!kanbanMode && (type == 'blocker' || type == 'milestone') && (
                                <span
                                    style={typeStyles}
                                    className="f-todo-task-item__project-type f-ellipsis f-text xs">
                                    {type}
                                </span>
                            )}

                            {showProjectName && !!projectName && (
                                <span
                                    style={{ '--f-todo-task-item-project-color': projectColor } as any}
                                    className="f-todo-task-item__project-name f-ellipsis f-text xs">
                                    {projectName}
                                </span>
                            )}

                            {edit && (
                                <div className="f-todo-task-item__editor">
                                    <TodoTaskEditor
                                        id={id}
                                        title={title}
                                        onIndent={onIndent}
                                        onOutdent={onOutdent}
                                        onSave={onEditSave}
                                        onDismiss={onEditCancel}
                                        selectOnFocus
                                        dontClearOnSubmit
                                    />
                                </div>
                            )}

                            {!edit && (
                                <>
                                    {!kanbanMode && (
                                        <div
                                            className="f-todo-task-item__text"
                                            dangerouslySetInnerHTML={{ __html: title }}
                                            onClick={(e) => {
                                                openLinkInText(e, () => {
                                                    stopEvent(e)
                                                    onEdit()
                                                })
                                            }}
                                        />
                                    )}

                                    {kanbanMode && (
                                        <div className="f-todo-task-item__text-kanban f-row">
                                            <Check
                                                priority={priority}
                                                onCheck={onComplete}
                                                checked={complete}
                                                type={type}
                                                id={id}
                                            />

                                            <div
                                                className="f-todo-task-item__text"
                                                dangerouslySetInnerHTML={{ __html: title }}
                                                onClick={(e) => {
                                                    openLinkInText(e, () => {
                                                        stopEvent(e)
                                                        onEdit()
                                                    })
                                                }}
                                            />
                                        </div>
                                    )}

                                    {!kanbanMode && !!subtaskAmount && (
                                        <span className="f-todo-task-item__subcount f-row">
                                            <Icon icon={TbCheckbox} />
                                            <span
                                                className="f-text sm"
                                                style={{ color: 'inherit' }}>
                                                {subtaskAmount}
                                            </span>
                                        </span>
                                    )}

                                    {!!externalEmail && (
                                        <span className="f-todo-task-item__widget f-row">
                                            <Icon icon={TbClick} />
                                        </span>
                                    )}

                                    {!kanbanMode && !!complexity && (
                                        <div className="f-todo-task-item__complexity">
                                            <Bars complexity={complexity} />
                                        </div>
                                    )}

                                    {attachments > 0 && !kanbanMode && (
                                        <span className="f-todo-task-item__attachments f-row">
                                            <Icon icon={TbPaperclip} />
                                        </span>
                                    )}

                                    {comments > 0 && !kanbanMode && (
                                        <span className="f-todo-task-item__comments f-row">
                                            <Icon icon={TbMessageCircle} />
                                        </span>
                                    )}

                                    {hasDescription && !kanbanMode && (
                                        <span className="f-todo-task-item__description f-row">
                                            <Icon icon={FIDescription} />
                                        </span>
                                    )}

                                    {!kanbanMode && !!points && (
                                        <div className="f-todo-task-item__points f-row f-text sm">{points}</div>
                                    )}

                                    {/*                  {!kanbanMode && (
                                        <span className="f-todo-task-item__likes f-row">
                                            <span className={isLiked ? 'f-like-button is-liked' : 'f-like-button'}>
                                                <Icon
                                                    icon={TbHeart}
                                                    style={{ color: isLiked ? 'var(--f-color-pink-400)' : undefined }}
                                                />
                                            </span>
                                        </span>
                                    )}  */}

                                    {!kanbanMode && (
                                        <button
                                            onClick={onOpen}
                                            className="f-todo-task-item__open f-button f-row">
                                            <Icon icon={TbExternalLink} />
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {showLabels && visibleFields.labels && (
                    <div
                        className="f-todo-task-item__labels f-row"
                        style={{ width: 'fit-content' }}>
                        <LabelButton
                            shorten={!kanbanMode}
                            portal={Portal}
                            labels={labels}
                            onAdd={onLabelAdd}
                            onDelete={onLabelDelete}
                            anchor={kanbanMode ? undefined : { vertical: 'bottom', horizontal: 'right' }}
                        />
                    </div>
                )}

                <div className="f-todo-task-item__tailend f-row">
                    {!noTaskDates && visibleFields.dates && (
                        <div className="f-todo-task-item__date f-row">
                            <DateButton
                                portal={Portal}
                                complete={complete}
                                start={start}
                                end={end}
                                repeat={repeat}
                                wholeDay={wholeDay}
                                onDateChange={onDateChange}
                                kanbanMode={kanbanMode}
                                onDelete={() => {
                                    onDateChange({
                                        start: null,
                                        end: null,
                                        repeat: null,
                                        wholeDay: null,
                                    })
                                }}
                            />
                        </div>
                    )}

                    {kanbanMode && !!points && <div className="f-todo-task-item__points f-row f-text sm">{points}</div>}

                    {kanbanMode && !!complexity && (
                        <div className="f-todo-task-item__complexity">
                            <Bars complexity={complexity} />
                        </div>
                    )}

                    {hasDescription && kanbanMode && (
                        <span className="f-todo-task-item__description f-row">
                            <Icon icon={FIDescription} />
                        </span>
                    )}

                    {attachments > 0 && kanbanMode && (
                        <span className="f-todo-task-item__attachments f-row">
                            <Icon icon={TbPaperclip} />
                        </span>
                    )}

                    {comments > 0 && kanbanMode && (
                        <span className="f-todo-task-item__comments f-row">
                            <Icon icon={TbMessageCircle} />
                        </span>
                    )}

                    {kanbanMode && !!subtaskAmount && (
                        <span className="f-todo-task-item__subcount f-row">
                            <Icon icon={TbCheckbox} />
                            <span
                                className="f-text sm"
                                style={{ color: 'inherit' }}>
                                {subtaskAmount}
                            </span>
                        </span>
                    )}

                    {!noTaskUsers && visibleFields.assigned && (
                        <div className="f-todo-task-item__users f-row">
                            <UserButton
                                portal={Portal}
                                users={users}
                                onAdd={onUserAdd}
                                onDelete={onUserDelete}
                                userSelectProps={{ context: { teamId, projectId } }}
                                anchor={kanbanMode ? undefined : { vertical: 'bottom', horizontal: 'right' }}
                            />
                        </div>
                    )}
                </div>

                {/*   
                <div
                    onClick={(e) => stopEvent(e)}
                    className="f-todo-task-item__footer f-row">   
                    {badges.map((badge, index) => (
                        <Badge
                            key={index}
                            label={badge.label}
                            icon={badge.icon}
                            color={badge.color}
                            progress={badge.progress}
                        />
                    ))}

                    <PriorityButton
                        portal={Portal}
                        priority={priority}
                        onChange={onPriorityChange}
                    />
                </div>
                */}
            </div>
        </div>
    )
}
